"use client";

import * as React from "react";
import { ZoomIn, ZoomOut } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Slider } from "@/components/ui/slider";

const VIEWPORT = 256; // square crop window, in display px
const MAX_OUTPUT = 512; // exported square edge (capped to the source resolution)
const MAX_ZOOM = 3;

type AvatarCropperProps = {
  /** Data URL of the picked image. */
  src: string;
  /** Called with a square JPEG data URL once the user applies the crop. */
  onCropped: (dataUrl: string) => void;
  onCancel: () => void;
  busy?: boolean;
};

/**
 * Self-contained avatar cropper: pan by dragging, zoom with the slider or
 * wheel, and export the visible square region as a JPEG data URL. No external
 * dependency — the crop math and rendering are done with a canvas.
 */
export function AvatarCropper({ src, onCropped, onCancel, busy }: AvatarCropperProps) {
  const imgRef = React.useRef<HTMLImageElement>(null);
  const [nat, setNat] = React.useState<{ w: number; h: number } | null>(null);
  const [zoom, setZoom] = React.useState(1);
  const [offset, setOffset] = React.useState({ x: 0, y: 0 });
  const drag = React.useRef<{ px: number; py: number; ox: number; oy: number } | null>(null);

  const baseScale = nat ? VIEWPORT / Math.min(nat.w, nat.h) : 1;
  const dispScale = baseScale * zoom;
  const dispW = nat ? nat.w * dispScale : 0;
  const dispH = nat ? nat.h * dispScale : 0;

  const clamp = React.useCallback(
    (x: number, y: number) => ({
      x: Math.min(0, Math.max(VIEWPORT - dispW, x)),
      y: Math.min(0, Math.max(VIEWPORT - dispH, y)),
    }),
    [dispW, dispH],
  );

  const handleLoad = () => {
    const el = imgRef.current;
    if (!el) return;
    const w = el.naturalWidth;
    const h = el.naturalHeight;
    setNat({ w, h });
    const scale = VIEWPORT / Math.min(w, h);
    // Center the image within the viewport at zoom 1.
    setOffset({ x: (VIEWPORT - w * scale) / 2, y: (VIEWPORT - h * scale) / 2 });
    setZoom(1);
  };

  const applyZoom = (nextZoom: number) => {
    if (!nat) return;
    const z = Math.min(MAX_ZOOM, Math.max(1, nextZoom));
    const oldScale = baseScale * zoom;
    const newScale = baseScale * z;
    // Keep the point under the viewport centre fixed while zooming.
    const cx = (VIEWPORT / 2 - offset.x) / oldScale;
    const cy = (VIEWPORT / 2 - offset.y) / oldScale;
    const nextW = nat.w * newScale;
    const nextH = nat.h * newScale;
    const raw = { x: VIEWPORT / 2 - cx * newScale, y: VIEWPORT / 2 - cy * newScale };
    setZoom(z);
    setOffset({
      x: Math.min(0, Math.max(VIEWPORT - nextW, raw.x)),
      y: Math.min(0, Math.max(VIEWPORT - nextH, raw.y)),
    });
  };

  const onPointerDown = (e: React.PointerEvent) => {
    e.currentTarget.setPointerCapture(e.pointerId);
    drag.current = { px: e.clientX, py: e.clientY, ox: offset.x, oy: offset.y };
  };
  const onPointerMove = (e: React.PointerEvent) => {
    if (!drag.current) return;
    const next = clamp(
      drag.current.ox + (e.clientX - drag.current.px),
      drag.current.oy + (e.clientY - drag.current.py),
    );
    setOffset(next);
  };
  const onPointerUp = (e: React.PointerEvent) => {
    drag.current = null;
    e.currentTarget.releasePointerCapture(e.pointerId);
  };

  const onWheel = (e: React.WheelEvent) => {
    applyZoom(zoom - e.deltaY * 0.002);
  };

  const handleApply = () => {
    const el = imgRef.current;
    if (!el || !nat) return;
    const cropNatural = VIEWPORT / dispScale; // source square edge, in natural px
    const srcX = -offset.x / dispScale;
    const srcY = -offset.y / dispScale;
    const out = Math.max(96, Math.round(Math.min(MAX_OUTPUT, cropNatural)));

    const canvas = document.createElement("canvas");
    canvas.width = out;
    canvas.height = out;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    ctx.fillStyle = "#ffffff"; // opaque backing for JPEG (no black on transparency)
    ctx.fillRect(0, 0, out, out);
    ctx.imageSmoothingQuality = "high";
    ctx.drawImage(el, srcX, srcY, cropNatural, cropNatural, 0, 0, out, out);
    onCropped(canvas.toDataURL("image/jpeg", 0.9));
  };

  return (
    <div className="flex flex-col items-center gap-4">
      <div
        className="relative cursor-grab touch-none select-none overflow-hidden rounded-full bg-muted ring-2 ring-border active:cursor-grabbing"
        style={{ width: VIEWPORT, height: VIEWPORT }}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerLeave={onPointerUp}
        onWheel={onWheel}
      >
        {/* eslint-disable-next-line jsx-a11y/img-redundant-alt */}
        <img
          ref={imgRef}
          src={src}
          alt="Crop preview"
          draggable={false}
          onLoad={handleLoad}
          style={{
            position: "absolute",
            left: offset.x,
            top: offset.y,
            width: dispW || undefined,
            height: dispH || undefined,
            maxWidth: "none",
          }}
        />
      </div>

      <div className="flex w-full max-w-xs items-center gap-3">
        <ZoomOut size={16} className="text-muted-foreground" />
        <Slider
          value={[zoom]}
          min={1}
          max={MAX_ZOOM}
          step={0.01}
          onValueChange={([v]) => applyZoom(v)}
          aria-label="Zoom"
        />
        <ZoomIn size={16} className="text-muted-foreground" />
      </div>

      <p className="text-xs text-muted-foreground">Drag to reposition, scroll or use the slider to zoom.</p>

      <div className="flex gap-2">
        <Button type="button" onClick={handleApply} disabled={busy || !nat}>
          Apply
        </Button>
        <Button type="button" variant="ghost" onClick={onCancel} disabled={busy}>
          Cancel
        </Button>
      </div>
    </div>
  );
}
